import React from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { useMetaAndAssetCtxs } from '../hooks/useMetaAndAssetCtxs';
import { useUiPreferences } from '../contexts/UiPreferencesContext';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

const pages = [
  { name: 'Dashboard', path: '/dashboard' },
  { name: 'Live Market', path: '/live-market' },
  { name: 'Perps Data', path: '/perps-data' },
  { name: 'Market Trends', path: '/market-trends' },
  { name: 'Trade History', path: '/trade-history' },
  { name: 'Open Orders', path: '/open-orders' },
  { name: 'TATAF', path: '/tataf' },
  { name: 'Portfolio', path: '/portfolio' },
  { name: 'Settings', path: '/settings' },
  { name: 'Help', path: '/help' },
];

const groupHeadingStyle: React.CSSProperties = {
  padding: '12px 14px 6px',
  fontSize: 9.5,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--muted)',
  fontFamily: 'var(--sans)',
};

const itemBaseStyle: React.CSSProperties = {
  padding: '8px 14px',
  fontSize: 13,
  color: 'var(--text)',
  cursor: 'pointer',
  fontFamily: 'var(--mono)',
  letterSpacing: '0.02em',
};

export default function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { data } = useMetaAndAssetCtxs();
  const { toggleDensity, toggleContrast, toggleTheme, theme } = useUiPreferences();

  const coins = data?.universe?.map((u) => u.name) || [];

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100 }} onClick={onClose} role="presentation">
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.55)',
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: '18%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: 560,
          padding: '0 16px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Command
          className="panel"
          style={{ overflow: 'hidden', boxShadow: '0 24px 60px -12px rgba(0,0,0,0.6)' }}
        >
          <div
            style={{
              borderBottom: '1px solid var(--rule)',
              background: 'var(--ink-3)',
            }}
          >
            <Command.Input
              placeholder="Search coins, pages, actions…"
              autoFocus
              style={{
                width: '100%',
                background: 'transparent',
                color: 'var(--text)',
                padding: '14px 18px',
                fontSize: 14,
                border: 'none',
                outline: 'none',
                fontFamily: 'var(--sans)',
              }}
            />
          </div>

          <Command.List style={{ maxHeight: 340, overflowY: 'auto' }}>
            <Command.Empty
              style={{
                padding: '20px',
                textAlign: 'center',
                color: 'var(--muted)',
                fontSize: 12,
                fontFamily: 'var(--mono)',
                letterSpacing: '0.06em',
              }}
            >
              ◆ NO RESULTS
            </Command.Empty>

            <Command.Group>
              <div style={groupHeadingStyle}>§ COINS</div>
              {coins.map((coin) => (
                <Command.Item
                  key={coin}
                  value={coin}
                  onSelect={() => {
                    navigate(`/coin/${coin}`);
                    onClose();
                  }}
                  style={itemBaseStyle}
                  className="cmd-item"
                >
                  {coin}
                </Command.Item>
              ))}
            </Command.Group>

            <Command.Group>
              <div style={groupHeadingStyle}>§ PAGES</div>
              {pages.map((page) => (
                <Command.Item
                  key={page.path}
                  value={page.name}
                  onSelect={() => {
                    navigate(page.path);
                    onClose();
                  }}
                  style={itemBaseStyle}
                  className="cmd-item"
                >
                  {page.name}
                </Command.Item>
              ))}
            </Command.Group>

            <Command.Group>
              <div style={groupHeadingStyle}>§ ACTIONS</div>
              <Command.Item
                value="Toggle theme"
                onSelect={() => {
                  toggleTheme();
                  onClose();
                }}
                style={itemBaseStyle}
                className="cmd-item"
              >
                Toggle theme · {theme === 'dark' ? 'switch to light' : 'switch to dark'}
              </Command.Item>
              <Command.Item
                value="Toggle density"
                onSelect={() => {
                  toggleDensity();
                  onClose();
                }}
                style={itemBaseStyle}
                className="cmd-item"
              >
                Toggle density
              </Command.Item>
              <Command.Item
                value="Toggle contrast"
                onSelect={() => {
                  toggleContrast();
                  onClose();
                }}
                style={itemBaseStyle}
                className="cmd-item"
              >
                Toggle contrast
              </Command.Item>
            </Command.Group>
          </Command.List>
        </Command>
      </div>
    </div>
  );
}
